import { Routes, Route, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/services/api'
import { CardSkeleton, PlanCardSkeleton } from '@/components/SkeletonLoader'
import { Check, CheckCircle2, XCircle, AlertCircle, Plug, Rss, Package, Banknote, CalendarDays, BarChart3, ArrowRight, FileText, Globe, Minus, Clock, Table } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Modal } from '@/components/Modal'
import { AlertModal, AlertType } from '@/components/AlertModal'
import InventoryPage from './InventoryPage'
import ProductManagePage from './ProductManagePage'
// AddProductPage removed — products synced from provider
import EditProductPage from './EditProductPage'
import FeedProductsPage from './FeedProductsPage'
import IntegrationsPage from './IntegrationsPage'
import BookingsPage from './BookingsPage'
import AnalyticsPage from './AnalyticsPage'
import SettingsPage from './SettingsPage'
import SupplierAgreementPage from './SupplierAgreementPage'
import OAuthClientsPage from './OAuthClientsPage'
import AiConnectionsPage from './AiConnectionsPage'
import AddOnsPage from './AddOnsPage'
import AgentSettingsPage from './AgentSettingsPage'
import AgentTranscriptsPage from './AgentTranscriptsPage'
import HelpPage from './HelpPage'
import { STRIPE_CONNECT_COUNTRIES, isStripeConnectSupported } from '@/constants/stripeConnectCountries'

function AddonPlaceholder({ feature }: { feature: string }) {
  return (
    <div style={{ maxWidth: '500px', margin: '3rem auto', textAlign: 'center' }}>
      <div style={{
        display: 'inline-block',
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        background: 'var(--color-gray-100)',
        color: 'var(--color-gray-500)',
        padding: '0.25rem 0.6rem',
        borderRadius: '4px',
        marginBottom: '1rem',
      }}>
        Addon
      </div>
      <h2 style={{ marginBottom: '0.75rem' }}>{feature}</h2>
      <p style={{ color: 'var(--color-gray-500)', lineHeight: 1.6 }}>
        This feature is available as an addon. Contact us to enable it for your account.
      </p>
    </div>
  )
}

function TrustScoreCard() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId

  const { data: trustData, isLoading, error } = useQuery({
    queryKey: ['trust-score', vendorId],
    queryFn: () => api.vendors.getTrustScore(vendorId!),
    staleTime: 300_000,
    enabled: !!vendorId,
    retry: false,
  })

  const score = trustData?.data?.data as {
    score: number
    label: string
    breakdown: Record<string, number>
    recommendations: string[]
    updated_at: string
  } | undefined

  const isGated = error && (error as any)?.response?.status === 403

  // Color based on score label
  const scoreColor = !score ? 'var(--color-gray-400)' :
    score.score >= 90 ? 'var(--color-lime-green)' :
      score.score >= 75 ? 'var(--color-sky-blue)' :
        score.score >= 50 ? 'var(--color-sunshine-yellow)' :
          'var(--color-coral-red)'

  const breakdownLabels: Record<string, string> = {
    verification: 'Verification',
    payment_reliability: 'Payment Reliability',
    booking_completion: 'Booking Completion',
    refund_rate: 'Refund Rate',
    tenure: 'Tenure',
    catalog_completeness: 'Catalog',
  }

  if (isGated) {
    return (
      <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
        <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.75rem' }}>
          Trust Score
        </h3>
        <p style={{ fontSize: 'var(--text-3xl)', color: 'var(--color-gray-300)', margin: '0 0 0.5rem' }}>--</p>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginBottom: '1rem' }}>
          Upgrade to Pro to see your Trust Score and AI ranking insights
        </p>
        <a href="https://tixxly.ai/pricing" style={{
          display: 'inline-block',
          padding: '0.5rem 1.5rem',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-pure-white)',
          background: 'var(--color-tixxly-navy)',
          borderRadius: '6px',
          textDecoration: 'none',
        }}>
          Upgrade to Pro
        </a>
      </div>
    )
  }

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
        {/* Score circle */}
        <div style={{ textAlign: 'center', minWidth: '120px' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.75rem' }}>
            Trust Score
          </h3>
          {isLoading ? (
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--color-gray-100)', margin: '0 auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ) : score ? (
            <>
              <div style={{
                width: '80px', height: '80px', borderRadius: '50%',
                border: `4px solid ${scoreColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto',
              }}>
                <span style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-gray-900)' }}>
                  {score.score}
                </span>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: scoreColor, marginTop: '0.5rem' }}>
                {score.label}
              </p>
            </>
          ) : (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)' }}>No data yet</p>
          )}
        </div>

        {/* Breakdown bars */}
        {score && (
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h4 style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
              Breakdown
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {Object.entries(score.breakdown).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', width: '120px', flexShrink: 0 }}>
                    {breakdownLabels[key] || key}
                  </span>
                  <div style={{ flex: 1, height: '6px', background: 'var(--color-gray-100)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${value}%`, height: '100%', borderRadius: '3px',
                      background: value >= 75 ? 'var(--color-lime-green)' : value >= 50 ? 'var(--color-sky-blue)' : 'var(--color-coral-red)',
                    }} />
                  </div>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', width: '30px', textAlign: 'right' }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {score && score.recommendations.length > 0 && (
          <div style={{ minWidth: '200px', maxWidth: '300px' }}>
            <h4 style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
              Recommendations
            </h4>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {score.recommendations.map((rec, i) => (
                <li key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {score?.updated_at && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginTop: '1rem', textAlign: 'right' }}>
          Updated {new Date(score.updated_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
        </p>
      )}
    </div>
  )
}

const ONBOARDING_STEPS = [
  { key: 'connect_provider', title: 'Connect a Provider', description: 'Connect your booking system to import your inventory.', actionLabel: 'Connect Provider', actionLink: '/vendor/integrations', icon: Plug },
  { key: 'sync_feeds', title: 'Sync Feeds', description: 'Import your product feeds from your connected provider.', actionLabel: 'View Feeds', actionLink: '/vendor/inventory', icon: Rss },
  { key: 'sync_experiences', title: 'Sync Experiences', description: 'Sync your tours and experiences so customers can discover and book them.', actionLabel: 'View Experiences', actionLink: '/vendor/inventory/experiences', icon: Package },
  { key: 'connect_payment', title: 'Connect Payment', description: 'Link your payment account to start accepting payments from customers.', actionLabel: 'Set Up Payouts', actionLink: '/vendor/payments/link-account', icon: Banknote },
  { key: 'accept_agreement', title: 'Accept Supplier Agreement', description: 'Review and accept the supplier agreement to start selling.', actionLabel: 'View Agreement', actionLink: '/vendor/supplier-agreement', icon: FileText },
  { key: 'setup_ai_storefront', title: 'Set Up AI Storefront', description: 'Configure your storefront so AI agents can send customers to book.', actionLabel: 'Set Up Storefront', actionLink: '/vendor/ai-connections', icon: Globe },
]

const STOREFRONT_PREREQUISITES_DASHBOARD = ['connect_provider', 'sync_feeds', 'sync_experiences', 'connect_payment', 'accept_agreement']

const QUICK_LINKS = [
  { label: 'View Bookings', description: 'Check and manage your bookings', to: '/vendor/bookings', icon: CalendarDays },
  { label: 'Manage Experiences', description: 'Edit your tours and activities', to: '/vendor/inventory/experiences', icon: Package },
  { label: 'View Analytics', description: 'See how your business is performing', to: '/vendor/analytics', icon: BarChart3 },
]

function VendorOverview() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId

  const { data: onboardingData, isLoading: onboardingLoading } = useQuery({
    queryKey: ['onboarding-status', vendorId],
    queryFn: () => api.vendors.getOnboardingStatus(vendorId!.toString()),
    enabled: !!vendorId,
    staleTime: 30_000,
  })

  const onboarding = (onboardingData as any)?.data?.data as {
    steps: { id: string; completed: boolean }[]
    completed: boolean
  } | undefined

  const isOnboardingDone = onboarding?.completed

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['ledger-summary'],
    queryFn: () => api.ledger.getSummary(),
    staleTime: 60_000,
  })

  const summary = (ledgerData as any)?.data?.data as {
    total_payments: number
    total_refunds: number
    total_fees: number
    net_revenue: number
    currency_breakdown: Record<string, { payments: number; refunds: number; fees: number; net: number }>
  } | undefined

  const primaryCurrency = summary ? Object.keys(summary.currency_breakdown)[0] || 'USD' : 'USD'
  const fmt = (amount: number, currency = primaryCurrency) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount)

  // Onboarding progress
  const totalSteps = onboarding?.steps?.length || ONBOARDING_STEPS.length
  const completedSteps = onboarding?.steps?.filter(s => s.completed).length || 0
  const progressPct = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0

  return (
    <div>
      {/* Onboarding section */}
      {!onboardingLoading && onboarding && !isOnboardingDone && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)' }}>Get Started</h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
              Complete these steps to start selling on Tixxly
            </p>
          </div>

          {/* Progress bar */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>{completedSteps} of {totalSteps} complete</span>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>{progressPct}%</span>
            </div>
            <div style={{ height: '6px', background: 'var(--color-gray-100)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--color-sky-blue)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
            </div>
          </div>

          {/* Step cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {ONBOARDING_STEPS.map(step => {
              const status = onboarding.steps?.find(s => s.id === step.key)
              const isDone = status?.completed
              const Icon = step.icon
              const isLocked = step.key === 'setup_ai_storefront' && !isDone &&
                STOREFRONT_PREREQUISITES_DASHBOARD.some((prereq) => {
                  const prereqStatus = onboarding.steps?.find((s: any) => s.id === prereq)
                  return prereqStatus && !prereqStatus.completed
                })

              return (
                <div key={step.key} className="card" style={{
                  padding: '1rem 1.25rem',
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem',
                  opacity: isDone ? 0.5 : isLocked ? 0.5 : 1,
                }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isDone ? 'var(--color-gray-100)' : isLocked ? 'var(--color-gray-100)' : 'rgba(56, 189, 248, 0.1)',
                    color: isDone ? 'var(--color-gray-400)' : isLocked ? 'var(--color-gray-300)' : 'var(--color-sky-blue)',
                    flexShrink: 0,
                  }}>
                    {isDone ? <CheckCircle2 size={18} /> : <Icon size={18} />}
                  </div>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <p style={{
                      margin: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
                      color: isDone ? 'var(--color-gray-400)' : isLocked ? 'var(--color-gray-400)' : 'var(--color-gray-800)',
                      textDecoration: isDone ? 'line-through' : 'none',
                    }}>
                      {step.title}
                      {status?.completed && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-lime-green)', marginLeft: '0.5rem', textDecoration: 'none', display: 'inline-block' }}>(done)</span>}
                      {isLocked && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginLeft: '0.5rem' }}>Complete previous steps first</span>}
                    </p>
                    <p style={{
                      margin: '0.125rem 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)',
                      textDecoration: isDone ? 'line-through' : 'none',
                    }}>{step.description}</p>
                  </div>
                  {!isDone && !isLocked && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: 'auto' }}>
                      <Link
                        to={step.actionLink}
                        style={{
                          padding: '0.375rem 0.75rem', fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)',
                          borderRadius: '6px', textDecoration: 'none',
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.actionLabel} <ArrowRight size={12} />
                      </Link>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Quick links (shown when onboarding complete) */}
      {!onboardingLoading && isOnboardingDone && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)' }}>Quick Links</h2>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: '1rem' }}>
            {QUICK_LINKS.map(link => {
              const Icon = link.icon
              return (
                <Link key={link.to} to={link.to} className="card" style={{
                  padding: '1.25rem', textDecoration: 'none', color: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  transition: 'box-shadow 0.15s',
                }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(56, 189, 248, 0.1)', color: 'var(--color-sky-blue)',
                    flexShrink: 0,
                  }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-800)' }}>{link.label}</p>
                    <p style={{ margin: '0.125rem 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>{link.description}</p>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Revenue cards */}
      <h2 style={{ margin: '0 0 1rem', fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)' }}>Revenue</h2>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '1.5rem' }}>
        <div className="card" style={{
          background: 'linear-gradient(135deg, var(--color-sky-blue) 0%, var(--color-tixxly-navy) 100%)',
          color: 'var(--color-pure-white)', border: 'none',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}>
          <h3 style={{ color: 'var(--color-pure-white)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-bold)', opacity: 0.9, marginBottom: '0.5rem' }}>
            Total Revenue
          </h3>
          {ledgerLoading ? (
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite', height: '2.5rem', width: '60%', marginBottom: '0.5rem' }} />
          ) : (
            <p style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-pure-white)', margin: 0 }}>
              {fmt(summary?.net_revenue ?? 0)}
            </p>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-pure-white)', opacity: 0.8, marginTop: '0.5rem' }}>Net all time</p>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.5rem' }}>Gross Payments</h3>
          {ledgerLoading ? (
            <div style={{ height: '2rem', width: '50%', background: 'var(--color-gray-100)', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ) : (
            <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-gray-900)', margin: 0 }}>{fmt(summary?.total_payments ?? 0)}</p>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginTop: '0.5rem' }}>All time</p>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.5rem' }}>Refunds</h3>
          {ledgerLoading ? (
            <div style={{ height: '2rem', width: '40%', background: 'var(--color-gray-100)', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ) : (
            <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: summary?.total_refunds ? 'var(--color-coral-red)' : 'var(--color-gray-900)', margin: 0 }}>{fmt(summary?.total_refunds ?? 0)}</p>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginTop: '0.5rem' }}>All time</p>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.5rem' }}>Platform Fees</h3>
          {ledgerLoading ? (
            <div style={{ height: '2rem', width: '40%', background: 'var(--color-gray-100)', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ) : (
            <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-gray-900)', margin: 0 }}>{fmt(summary?.total_fees ?? 0)}</p>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginTop: '0.5rem' }}>All time</p>
        </div>

        <TrustScoreCard />
      </div>
    </div>
  )
}

function PaymentsLinkAccount() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
    confirmText?: string
    cancelText?: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  // Email + country form state — required by Stripe before account creation.
  // Pre-filled from contact_info; vendor can correct before submitting.
  const [email, setEmail] = useState('')
  const [country, setCountry] = useState('')
  const [legalEntityType, setLegalEntityType] = useState<'individual' | 'company' | ''>('')
  const [formTouched, setFormTouched] = useState(false)
  const [hasPrefilled, setHasPrefilled] = useState(false)

  // Disconnect confirmation
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false)

  // Stripe Status
  const { data: stripeStatus, isLoading: isLoadingStripe, refetch: refetchStripeStatus } = useQuery({
    queryKey: ['stripe-status', vendorId],
    queryFn: () => api.vendors.getStripeStatus(vendorId!),
    enabled: !!vendorId,
  })

  // Vendor data for prefill
  const { data: vendorData } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => api.vendors.get(vendorId!),
    enabled: !!vendorId,
  })

  const vendorContactInfo = (() => {
    const v = vendorData?.data?.data || vendorData?.data
    const ci = typeof v?.contact_info === 'string' ? JSON.parse(v.contact_info) : v?.contact_info
    return ci || {}
  })()

  // Prefill once when vendor data arrives so the vendor's edits aren't blown away
  useEffect(() => {
    // Wait for both queries — vendor data has the contact_info fallback, stripe
    // status has the dedicated provider_account_* fields (preferred source).
    if (hasPrefilled || !vendorData || !stripeStatus) return
    const stripeAccountData = stripeStatus?.data?.data
    const prefillEmail =
      stripeAccountData?.accountEmail ||
      vendorContactInfo?.email ||
      user?.email ||
      ''
    const prefillCountry = (
      stripeAccountData?.accountCountry ||
      vendorContactInfo?.address?.country ||
      vendorContactInfo?.address?.country_code ||
      ''
    )
      .toString()
      .toUpperCase()
      .trim()
    setEmail(prefillEmail)
    setCountry(prefillCountry)
    const prefillEntity = stripeAccountData?.accountEntityType
    if (prefillEntity === 'individual' || prefillEntity === 'company') setLegalEntityType(prefillEntity)
    setHasPrefilled(true)
  }, [vendorData, stripeStatus, hasPrefilled, vendorContactInfo, user?.email])

  const trimmedEmail = email.trim()
  const normalizedCountry = country.toUpperCase().trim()
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)
  const isCountrySelected = normalizedCountry.length === 2
  const isCountrySupported = isStripeConnectSupported(normalizedCountry)
  const showCountryUnsupported = isCountrySelected && !isCountrySupported
  const canSubmit = isValidEmail && isCountrySupported && legalEntityType !== ''

  // Handle Stripe return
  useEffect(() => {
    const stripeParam = searchParams.get('stripe')
    if (stripeParam === 'success') {
      posthog.capture('payment_setup_returned', { status: 'success' })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Stripe account connected successfully!',
      })
      setSearchParams({})
      refetchStripeStatus()
    } else if (stripeParam === 'refresh') {
      posthog.capture('payment_setup_returned', { status: 'refresh' })
      refetchStripeStatus()
    }
  }, [searchParams, setSearchParams, refetchStripeStatus])

  // Connect Stripe Mutation
  const connectStripeMutation = useMutation({
    mutationFn: () => {
      posthog.capture('payment_setup_started')
      const returnUrl = `${window.location.origin}/vendor/payments/link-account?stripe=success`
      const refreshUrl = `${window.location.origin}/vendor/payments/link-account?stripe=refresh`
      return api.vendors.connectStripe(vendorId!, {
        email: trimmedEmail,
        country: normalizedCountry,
        legalEntityType: legalEntityType || undefined,
        returnUrl,
        refreshUrl,
      })
    },
    onSuccess: (response) => {
      const { onboardingUrl } = response.data.data
      if (onboardingUrl) {
        window.location.href = onboardingUrl
      }
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || 'Failed to connect Stripe account'
      posthog.capture('payment_setup_failed', { error: msg })
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: msg,
      })
    },
  })

  // Refresh Stripe Onboarding
  const refreshStripeMutation = useMutation({
    mutationFn: () => {
      const returnUrl = `${window.location.origin}/vendor/payments/link-account?stripe=success`
      const refreshUrl = `${window.location.origin}/vendor/payments/link-account?stripe=refresh`
      return api.vendors.refreshStripeOnboarding(vendorId!, { returnUrl, refreshUrl })
    },
    onSuccess: (response) => {
      const { onboardingUrl } = response.data.data
      if (onboardingUrl) {
        window.location.href = onboardingUrl
      }
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to refresh Stripe onboarding',
      })
    },
  })

  // Disconnect Stripe
  const disconnectStripeMutation = useMutation({
    mutationFn: () => api.vendors.disconnectStripe(vendorId!),
    onSuccess: () => {
      posthog.capture('payment_setup_disconnected')
      queryClient.invalidateQueries({ queryKey: ['stripe-status', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', vendorId] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Disconnected',
        message: 'Your Stripe account has been disconnected. You can connect a new one any time.',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to disconnect Stripe account',
      })
    },
  })

  const stripeData = stripeStatus?.data?.data
  const isStripeConnected = stripeData?.connected || false
  const isOnboardingComplete = stripeData?.onboardingComplete || false
  const chargesEnabled = stripeData?.chargesEnabled || false
  const payoutsEnabled = stripeData?.payoutsEnabled || false

  if (isLoadingStripe) {
    return (
      <div>
        <h2 style={{ marginBottom: '2rem' }}>Manage</h2>
        <CardSkeleton />
      </div>
    )
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-gray-700)',
    marginBottom: '0.375rem',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.625rem 0.75rem',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-base)',
    fontFamily: 'inherit',
    background: 'var(--color-pure-white)',
    color: 'var(--color-gray-900)',
  }

  return (
    <div>
      <h2 style={{ marginBottom: '2rem' }}>Manage</h2>

      <div className="card" style={{ border: `1px solid var(--color-gray-200)`, marginBottom: '2rem' }}>
        {/* Stripe Subsection */}
        <div style={{ marginBottom: '2rem' }}>
          <h5 style={{
            margin: '0 0 1rem 0',
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-gray-700)'
          }}>
            Stripe
          </h5>

          {!isStripeConnected ? (
            <div>
              <p style={{ marginBottom: '1.5rem', color: 'var(--color-gray-600)', fontSize: 'var(--text-base)', lineHeight: 'var(--leading-relaxed)' }}>
                Tell us where your business is registered and the email Stripe should use. We'll then take you to Stripe to finish setup.
              </p>

              <div style={{ display: 'grid', gap: '1rem', maxWidth: '480px', marginBottom: '1.5rem' }}>
                <div>
                  <label htmlFor="stripe-email" style={labelStyle}>Business Email *</label>
                  <input
                    id="stripe-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setFormTouched(true) }}
                    placeholder="you@yourbusiness.com"
                    style={inputStyle}
                    autoComplete="email"
                  />
                  {formTouched && trimmedEmail && !isValidEmail && (
                    <p style={{ margin: '0.375rem 0 0', color: 'var(--color-coral-red)', fontSize: 'var(--text-xs)' }}>
                      Enter a valid email address.
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="stripe-country" style={labelStyle}>Country of Business *</label>
                  <select
                    id="stripe-country"
                    value={normalizedCountry}
                    onChange={(e) => { setCountry(e.target.value); setFormTouched(true) }}
                    style={inputStyle}
                  >
                    <option value="">Select a country…</option>
                    {STRIPE_CONNECT_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                    {showCountryUnsupported && (
                      <option value={normalizedCountry} disabled>
                        {normalizedCountry} (not supported)
                      </option>
                    )}
                  </select>
                  <p style={{ margin: '0.375rem 0 0', color: 'var(--color-gray-500)', fontSize: 'var(--text-xs)' }}>
                    Don't see your country? Stripe Connect doesn't support it yet — email <a href="mailto:hello@tixxly.ai" style={{ color: 'var(--color-tixxly-navy)' }}>hello@tixxly.ai</a> and we'll let you know when it launches.
                  </p>
                </div>

                <div>
                  <label htmlFor="stripe-entity-type" style={labelStyle}>Business Structure *</label>
                  <select
                    id="stripe-entity-type"
                    value={legalEntityType}
                    onChange={(e) => { setLegalEntityType(e.target.value as 'individual' | 'company' | ''); setFormTouched(true) }}
                    style={inputStyle}
                  >
                    <option value="">Select…</option>
                    <option value="individual">Individual / sole trader</option>
                    <option value="company">Registered company</option>
                  </select>
                  <p style={{ margin: '0.375rem 0 0', color: 'var(--color-gray-500)', fontSize: 'var(--text-xs)' }}>
                    Self-employed (e.g. autónomo, sole proprietor)? Choose Individual. Stripe will then ask for your personal ID instead of company incorporation documents.
                  </p>
                </div>
              </div>

              {showCountryUnsupported && (
                <div style={{
                  padding: '1.25rem',
                  background: 'rgba(255, 76, 91, 0.06)',
                  border: '1px solid rgba(255, 76, 91, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                }}>
                  <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--color-gray-800)' }}>
                    Stripe is not available in {normalizedCountry}
                  </p>
                  <p style={{ margin: 0, color: 'var(--color-gray-600)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                    Stripe Connect doesn't currently support businesses in your region. Pick a supported country above, or contact us at <a href="mailto:hello@tixxly.ai">hello@tixxly.ai</a>.
                  </p>
                </div>
              )}

              <button
                onClick={() => connectStripeMutation.mutate()}
                disabled={connectStripeMutation.isPending || !canSubmit}
                style={{
                  background: canSubmit ? 'var(--color-sky-blue)' : 'var(--color-gray-300)',
                  color: 'var(--color-pure-white)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-bold)',
                  padding: '0.75rem 1.5rem',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {connectStripeMutation.isPending ? 'Connecting...' : 'Connect Stripe Account'}
              </button>
            </div>
          ) : (
            <div>
              <div style={{
                padding: '1.25rem',
                backgroundColor: isOnboardingComplete ? 'rgba(152, 202, 63, 0.1)' : 'var(--color-gray-100)',
                borderRadius: 'var(--radius-md)',
                marginBottom: '1.5rem',
                border: `1px solid ${isOnboardingComplete ? 'var(--color-lime-green)' : 'var(--color-gray-300)'}`,
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  padding: '0.375rem 0.875rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  background: isOnboardingComplete ? 'var(--color-lime-green)' : 'var(--color-gray-500)',
                  color: 'var(--color-pure-white)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {isOnboardingComplete ? 'Active' : 'Setup Incomplete'}
                </span>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)', display: 'grid', gap: '0.75rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <strong style={{ color: 'var(--color-gray-900)', minWidth: '120px', flexShrink: 0 }}>Account ID:</strong>
                    <code style={{
                      background: 'var(--color-gray-100)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-xs)',
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--color-gray-900)',
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word',
                      display: 'inline-block',
                      width: 'fit-content',
                      maxWidth: '100%'
                    }}>
                      {stripeData?.stripeAccountId}
                    </code>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ color: 'var(--color-gray-900)', minWidth: '120px' }}>Charges Enabled:</strong>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      padding: '0.25rem 0.625rem',
                      borderRadius: 'var(--radius-md)',
                      background: chargesEnabled ? 'rgba(152, 202, 63, 0.1)' : 'rgba(255, 76, 91, 0.1)',
                      border: `1px solid ${chargesEnabled ? 'var(--color-lime-green)' : 'var(--color-coral-red)'}`,
                      width: 'fit-content'
                    }}>
                      {chargesEnabled ? (
                        <>
                          <CheckCircle2 size={16} color="var(--color-lime-green)" />
                          <span style={{
                            color: 'var(--color-lime-green)',
                            fontWeight: 'var(--font-weight-medium)',
                            fontSize: 'var(--text-xs)'
                          }}>Yes</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={16} color="var(--color-coral-red)" />
                          <span style={{
                            color: 'var(--color-coral-red)',
                            fontWeight: 'var(--font-weight-medium)',
                            fontSize: 'var(--text-xs)'
                          }}>No</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ color: 'var(--color-gray-900)', minWidth: '120px' }}>Payouts Enabled:</strong>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      padding: '0.25rem 0.625rem',
                      borderRadius: 'var(--radius-md)',
                      background: payoutsEnabled ? 'rgba(152, 202, 63, 0.1)' : 'rgba(255, 76, 91, 0.1)',
                      border: `1px solid ${payoutsEnabled ? 'var(--color-lime-green)' : 'var(--color-coral-red)'}`,
                      width: 'fit-content'
                    }}>
                      {payoutsEnabled ? (
                        <>
                          <CheckCircle2 size={16} color="var(--color-lime-green)" />
                          <span style={{
                            color: 'var(--color-lime-green)',
                            fontWeight: 'var(--font-weight-medium)',
                            fontSize: 'var(--text-xs)'
                          }}>Yes</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={16} color="var(--color-coral-red)" />
                          <span style={{
                            color: 'var(--color-coral-red)',
                            fontWeight: 'var(--font-weight-medium)',
                            fontSize: 'var(--text-xs)'
                          }}>No</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!isOnboardingComplete && (
                <div>
                  <p style={{ marginBottom: '1rem', color: 'var(--color-gray-600)', fontSize: 'var(--text-base)' }}>
                    Just a few more steps to finish your Stripe setup and start accepting payments.
                  </p>
                  <button
                    onClick={() => refreshStripeMutation.mutate()}
                    disabled={refreshStripeMutation.isPending}
                    style={{
                      background: 'var(--color-blue-500)',
                      color: 'var(--color-white)',
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-weight-medium)',
                      padding: '0.75rem 1.5rem'
                    }}
                  >
                    {refreshStripeMutation.isPending ? 'Loading...' : 'Complete Stripe Setup'}
                  </button>
                </div>
              )}

              {isOnboardingComplete && (
                <div style={{
                  padding: '1rem 1.25rem',
                  backgroundColor: 'rgba(152, 202, 63, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  marginTop: '1rem',
                  border: `1px solid var(--color-lime-green)`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <CheckCircle2 size={20} color="var(--color-lime-green)" style={{ flexShrink: 0 }} />
                  <p style={{ margin: 0, color: 'var(--color-tixxly-navy)', fontWeight: 'var(--font-weight-semibold)' }}>
                    You're all set! Your Stripe account is ready to accept payments.
                  </p>
                </div>
              )}

              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1.25rem',
                borderTop: '1px solid var(--color-gray-200)',
              }}>
                <p style={{ margin: '0 0 0.75rem', color: 'var(--color-gray-600)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                  Need to connect a different Stripe account? Disconnect this one first. The Stripe account itself is preserved and stays accessible from your Stripe dashboard.
                </p>
                <button
                  onClick={() => setDisconnectConfirmOpen(true)}
                  disabled={disconnectStripeMutation.isPending}
                  style={{
                    background: 'transparent',
                    color: 'var(--color-coral-red)',
                    border: '1px solid var(--color-coral-red)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    padding: '0.5rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    cursor: disconnectStripeMutation.isPending ? 'not-allowed' : 'pointer',
                  }}
                >
                  {disconnectStripeMutation.isPending ? 'Disconnecting...' : 'Disconnect Stripe Account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={alertModal.onConfirm}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />

      {/* Disconnect confirmation */}
      <AlertModal
        isOpen={disconnectConfirmOpen}
        onClose={() => setDisconnectConfirmOpen(false)}
        onConfirm={() => {
          disconnectStripeMutation.mutate()
          setDisconnectConfirmOpen(false)
        }}
        type="confirm"
        title="Disconnect Stripe Account"
        message="This unlinks Stripe from your Tixxly account. You won't be able to take new bookings until you reconnect a Stripe account. Continue?"
        confirmText="Disconnect"
        cancelText="Cancel"
      />
    </div>
  )
}

function PaymentsManage() {
  return (
    <div>
      <h2 style={{ marginBottom: '2rem' }}>Transactions</h2>
      <div className="card" style={{ border: `1px solid var(--color-gray-200)`, padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-base)' }}>
          Payment management features coming soon
        </p>
      </div>
    </div>
  )
}

function SubscriptionsTransactions() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const [page, setPage] = useState(1)
  const limit = 10
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set())
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data: transactionsData, isLoading, error } = useQuery({
    queryKey: ['subscription-transactions', vendorId, page],
    queryFn: () => api.vendors.getSubscriptionTransactions(vendorId!, { page, limit }),
    enabled: !!vendorId,
  })

  const transactions = transactionsData?.data?.data?.items || []
  const pagination = transactionsData?.data?.data?.pagination
  const total = pagination?.total || 0
  const totalPages = pagination?.pages || Math.ceil(total / limit)

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount)
  }

  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string | Date) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const handleTransactionClick = (transaction: any, e: React.MouseEvent) => {
    // Don't open modal if clicking on expand/collapse or copy buttons
    const target = e.target as HTMLElement
    if (target.closest('span[style*="cursor: pointer"]') || target.closest('button')) {
      return
    }
    setSelectedTransaction(transaction)
    setIsModalOpen(true)
  }

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      subscription_created: 'Subscription Created',
      renewal: 'Renewal',
      upgrade: 'Upgrade',
      downgrade: 'Downgrade',
      cancellation: 'Cancellation',
      refund: 'Refund',
    }
    return labels[type] || type
  }

  const formatAmount = (amount: number, currency: string = 'USD', transactionType: string) => {
    const formatted = formatCurrency(Math.abs(amount), currency)
    // Show refunds with negative sign or indicator
    if (transactionType === 'refund') {
      return `-${formatted}`
    }
    return formatted
  }

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      succeeded: 'var(--color-lime-green)',
      pending: 'var(--color-gray-500)',
      failed: 'var(--color-coral-red)',
      refunded: 'var(--color-gray-600)',
    }
    return colors[status] || 'var(--color-gray-500)'
  }

  if (isLoading) {
    return (
      <div>
        <h2 style={{ marginBottom: '2rem' }}>Transactions</h2>
        <CardSkeleton />
      </div>
    )
  }

  if (error) {
    console.error('Error loading subscription transactions:', error)
    return (
      <div>
        <h2 style={{ marginBottom: '2rem' }}>Transactions</h2>
        <div className="card" style={{ border: `1px solid var(--color-gray-200)`, padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-coral-red)', fontSize: 'var(--text-base)', marginBottom: '0.5rem' }}>
            Error loading transactions. Please try again.
          </p>
          {error instanceof Error && (
            <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-sm)', marginTop: '0.5rem' }}>
              {error.message}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginBottom: '2rem' }}>Transactions</h2>

      {transactions.length === 0 ? (
        <div className="card" style={{ border: `1px solid var(--color-gray-200)`, padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-base)' }}>
            No subscription transactions found.
          </p>
        </div>
      ) : (
        <>
          <div className="card" style={{ border: `1px solid var(--color-gray-200)`, marginBottom: '2rem' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid var(--color-gray-200)` }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Date
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Transaction ID
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Payment Channel ID
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Type
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Plan
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Source
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'right', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Amount
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction: any) => {
                    const hasChildren = transaction.children && transaction.children.length > 0
                    const isExpanded = expandedTransactions.has(transaction.transactionId)
                    const childrenCount = transaction.children?.length || 0

                    return (
                      <>
                        <tr
                          key={transaction.transactionId}
                          style={{
                            borderBottom: `1px solid var(--color-gray-100)`,
                            cursor: 'pointer',
                            backgroundColor: isExpanded ? 'var(--color-gray-50)' : 'transparent',
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded) {
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }
                          }}
                          onClick={(e) => {
                            const target = e.target as HTMLElement
                            // Don't trigger modal if clicking expand/collapse area, copy buttons, or transaction ID
                            const isExpandArea = target.closest('[data-expand-area]') !== null
                            const isCopyButton = target.closest('div[title]') !== null

                            if (isExpandArea || isCopyButton) {
                              // Handle expand/collapse
                              if (hasChildren && isExpandArea) {
                                e.stopPropagation()
                                setExpandedTransactions((prev) => {
                                  const newSet = new Set(prev)
                                  if (newSet.has(transaction.transactionId)) {
                                    newSet.delete(transaction.transactionId)
                                  } else {
                                    newSet.add(transaction.transactionId)
                                  }
                                  return newSet
                                })
                              }
                              return
                            }
                            handleTransactionClick(transaction, e)
                          }}
                        >
                          <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-900)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              {hasChildren ? (
                                <div data-expand-area style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                  <span
                                    style={{
                                      fontSize: 'var(--text-sm)',
                                      color: 'var(--color-sky-blue)',
                                      fontWeight: 'var(--font-weight-medium)',
                                      width: '20px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                  <div style={{
                                    width: '2px',
                                    height: '20px',
                                    backgroundColor: 'var(--color-sky-blue)',
                                    borderRadius: '1px',
                                  }} />
                                  <div>
                                    <div>{formatDate(transaction.createdAt)}</div>
                                    <div style={{
                                      fontSize: 'var(--text-xs)',
                                      color: 'var(--color-sky-blue)',
                                      marginTop: '0.25rem',
                                      textDecoration: 'underline',
                                    }}>
                                      {childrenCount} related transaction{childrenCount !== 1 ? 's' : ''}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span style={{ width: '20px', display: 'inline-block' }} />
                                  <div>
                                    <div>{formatDate(transaction.createdAt)}</div>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', fontSize: 'var(--text-xs)', color: 'var(--color-gray-700)', fontFamily: 'monospace' }}>
                            <div
                              style={{
                                maxWidth: '150px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                              title={transaction.transactionId}
                              onClick={(e) => {
                                e.stopPropagation()
                                navigator.clipboard.writeText(transaction.transactionId)
                              }}
                            >
                              {transaction.transactionId}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', fontFamily: 'monospace' }}>
                            {transaction.parentTransactionId ? (
                              <div
                                style={{
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  color: 'var(--color-sky-blue)',
                                }}
                                title={transaction.parentTransactionId}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigator.clipboard.writeText(transaction.parentTransactionId)
                                }}
                              >
                                {transaction.parentTransactionId}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--color-gray-400)', fontStyle: 'italic' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-900)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {getTransactionTypeLabel(transaction.transactionType)}
                              {hasChildren && (
                                <span
                                  style={{
                                    padding: '0.125rem 0.5rem',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: 'var(--font-weight-medium)',
                                    backgroundColor: 'var(--color-sky-blue)20',
                                    color: 'var(--color-sky-blue)',
                                  }}
                                >
                                  Parent
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-900)' }}>
                            {transaction.planDetails?.name || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-900)', textTransform: 'capitalize' }}>
                            {transaction.psp || 'stripe'}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: transaction.transactionType === 'refund' ? 'var(--color-coral-red)' : 'var(--color-gray-900)' }}>
                            {formatAmount(transaction.amount, transaction.currency, transaction.transactionType)}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span
                              style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--text-xs)',
                                fontWeight: 'var(--font-weight-medium)',
                                backgroundColor: `${getStatusBadgeColor(transaction.status)}20`,
                                color: getStatusBadgeColor(transaction.status),
                                textTransform: 'capitalize',
                              }}
                            >
                              {transaction.status}
                            </span>
                          </td>
                        </tr>
                        {isExpanded && hasChildren && transaction.children.map((child: any, childIndex: number) => (
                          <tr
                            key={child.transactionId}
                            style={{
                              borderBottom: childIndex === transaction.children.length - 1 ? `2px solid var(--color-gray-200)` : `1px solid var(--color-gray-100)`,
                              backgroundColor: 'var(--color-gray-50)',
                              borderLeft: '3px solid var(--color-sky-blue)',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--color-gray-100)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'
                            }}
                            onClick={(e) => {
                              const target = e.target as HTMLElement
                              // Don't trigger modal if clicking copy buttons
                              if (target.closest('div[title]')) {
                                return
                              }
                              handleTransactionClick(child, e)
                            }}
                          >
                            <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '2.5rem' }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: 'var(--color-sky-blue)',
                                  flexShrink: 0,
                                }} />
                                <div>
                                  <div>{formatDate(child.createdAt)}</div>
                                  <div style={{
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--color-gray-500)',
                                    marginTop: '0.25rem',
                                  }}>
                                    Related to parent
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '1rem', fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)', fontFamily: 'monospace' }}>
                              <div
                                style={{
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                                title={child.transactionId}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigator.clipboard.writeText(child.transactionId)
                                }}
                              >
                                {child.transactionId}
                              </div>
                            </td>
                            <td style={{ padding: '1rem', fontSize: 'var(--text-xs)', color: 'var(--color-sky-blue)', fontFamily: 'monospace' }}>
                              <div
                                style={{
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  fontWeight: 'var(--font-weight-medium)',
                                }}
                                title={child.parentTransactionId}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigator.clipboard.writeText(child.parentTransactionId)
                                }}
                              >
                                {child.parentTransactionId}
                              </div>
                            </td>
                            <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {getTransactionTypeLabel(child.transactionType)}
                                <span
                                  style={{
                                    padding: '0.125rem 0.5rem',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: 'var(--font-weight-medium)',
                                    backgroundColor: 'var(--color-gray-200)',
                                    color: 'var(--color-gray-600)',
                                    marginLeft: 'auto',
                                  }}
                                >
                                  Child
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                              {child.planDetails?.name || 'N/A'}
                            </td>
                            <td style={{ padding: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)', textTransform: 'capitalize' }}>
                              {child.psp || 'stripe'}
                            </td>
                            <td style={{ padding: '1rem', textAlign: 'right', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: child.transactionType === 'refund' ? 'var(--color-coral-red)' : 'var(--color-gray-700)' }}>
                              {formatAmount(child.amount, child.currency, child.transactionType)}
                            </td>
                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                              <span
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: 'var(--radius-md)',
                                  fontSize: 'var(--text-xs)',
                                  fontWeight: 'var(--font-weight-medium)',
                                  backgroundColor: `${getStatusBadgeColor(child.status)}20`,
                                  color: getStatusBadgeColor(child.status),
                                  textTransform: 'capitalize',
                                }}
                              >
                                {child.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '0.5rem 1rem',
                  background: page === 1 ? 'var(--color-gray-300)' : 'var(--color-sky-blue)',
                  color: 'var(--color-pure-white)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  background: page === totalPages ? 'var(--color-gray-300)' : 'var(--color-sky-blue)',
                  color: 'var(--color-pure-white)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Transaction Detail Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Transaction Details"
        maxWidth="800px"
      >
        {selectedTransaction && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Status and Type Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '1px solid var(--color-gray-200)' }}>
              <div>
                <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-900)' }}>
                  {getTransactionTypeLabel(selectedTransaction.transactionType)}
                </h3>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                  {selectedTransaction.planDetails?.name || 'N/A'}
                </p>
              </div>
              <span
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  backgroundColor: `${getStatusBadgeColor(selectedTransaction.status)}20`,
                  color: getStatusBadgeColor(selectedTransaction.status),
                  textTransform: 'capitalize',
                }}
              >
                {selectedTransaction.status}
              </span>
            </div>

            {/* Transaction Details Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Amount
                </label>
                <p style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', color: selectedTransaction.transactionType === 'refund' ? 'var(--color-coral-red)' : 'var(--color-gray-900)' }}>
                  {formatAmount(selectedTransaction.amount, selectedTransaction.currency, selectedTransaction.transactionType)}
                </p>
                {selectedTransaction.transactionType === 'refund' && selectedTransaction.metadata?.isFullRefund && (
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                    Full refund
                  </p>
                )}
                {selectedTransaction.transactionType === 'refund' && !selectedTransaction.metadata?.isFullRefund && (
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                    Partial refund (Original: {formatCurrency(selectedTransaction.metadata?.originalAmount || 0, selectedTransaction.currency)})
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Payment Source
                </label>
                <p style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-900)', textTransform: 'capitalize' }}>
                  {selectedTransaction.psp || 'stripe'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Transaction ID
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <code style={{
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'monospace',
                    color: 'var(--color-gray-700)',
                    background: 'var(--color-gray-100)',
                    padding: '0.25rem 0.5rem',
                    borderRadius: 'var(--radius-sm)',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {selectedTransaction.transactionId}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedTransaction.transactionId)
                    }}
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: 'var(--color-gray-200)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--color-gray-700)',
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>

              {selectedTransaction.parentTransactionId && (
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Payment Channel ID
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <code style={{
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'monospace',
                      color: 'var(--color-sky-blue)',
                      background: 'var(--color-sky-blue)10',
                      padding: '0.25rem 0.5rem',
                      borderRadius: 'var(--radius-sm)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {selectedTransaction.parentTransactionId}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedTransaction.parentTransactionId)
                      }}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: 'var(--color-sky-blue)20',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-sky-blue)',
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Created At
                </label>
                <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--color-gray-900)' }}>
                  {formatDateTime(selectedTransaction.createdAt)}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Updated At
                </label>
                <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--color-gray-900)' }}>
                  {formatDateTime(selectedTransaction.updatedAt)}
                </p>
              </div>

              {selectedTransaction.billingPeriodStart && (
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Billing Period Start
                  </label>
                  <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--color-gray-900)' }}>
                    {formatDateTime(selectedTransaction.billingPeriodStart)}
                  </p>
                </div>
              )}

              {selectedTransaction.billingPeriodEnd && (
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Billing Period End
                  </label>
                  <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--color-gray-900)' }}>
                    {formatDateTime(selectedTransaction.billingPeriodEnd)}
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            {selectedTransaction.description && (
              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Description
                </label>
                <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--color-gray-700)', lineHeight: 'var(--leading-relaxed)' }}>
                  {selectedTransaction.description}
                </p>
              </div>
            )}

            {/* Plan Details */}
            {selectedTransaction.planDetails && (
              <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--color-gray-200)' }}>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Plan Details
                </label>
                <div style={{
                  background: 'var(--color-gray-50)',
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                }}>
                  <div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>Tier:</span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-900)', marginLeft: '0.5rem', textTransform: 'capitalize' }}>
                      {selectedTransaction.planDetails.name || ({ 0: 'Free', 1: 'Growth', 2: 'Pro', 3: 'Scale', 4: 'Volume', 5: 'Infrastructure' } as Record<number, string>)[selectedTransaction.planDetails.tier] || `Tier ${selectedTransaction.planDetails.tier}`}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>Interval:</span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-900)', marginLeft: '0.5rem', textTransform: 'capitalize' }}>
                      {selectedTransaction.planDetails.interval}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>Price:</span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-900)', marginLeft: '0.5rem' }}>
                      {formatCurrency(selectedTransaction.planDetails.price, selectedTransaction.planDetails.currency)}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>Currency:</span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-900)', marginLeft: '0.5rem', textTransform: 'uppercase' }}>
                      {selectedTransaction.planDetails.currency}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Metadata */}
            {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
              <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--color-gray-200)' }}>
                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Metadata
                </label>
                <pre style={{
                  background: 'var(--color-gray-50)',
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'monospace',
                  color: 'var(--color-gray-700)',
                  overflow: 'auto',
                  margin: 0,
                }}>
                  {JSON.stringify(selectedTransaction.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

type ComparisonCell = boolean | string | { pending: true; label: string }

type PlanComparison = {
  tiers: { slug: string; name: string }[]
  rows: { feature: string; values: Record<string, ComparisonCell> }[]
}

function ComparisonValue({ value }: { value: ComparisonCell | undefined }) {
  if (value === true) {
    return <Check size={16} color="var(--color-lime-green)" aria-label="Included" />
  }
  if (value === false || value == null) {
    return <Minus size={16} color="var(--color-gray-400)" aria-label="Not included" />
  }
  if (typeof value === 'object') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', fontSize: 'var(--text-xs)', color: 'var(--color-gray-700)' }}>
        <Clock size={14} aria-hidden="true" />
        {value.label}
      </span>
    )
  }
  return <span>{value}</span>
}

function FeatureComparison({
  comparison,
  currentSlug,
}: {
  comparison: PlanComparison
  currentSlug?: string
}) {
  if (comparison.tiers.length === 0 || comparison.rows.length === 0) return null

  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-lg)', background: 'var(--color-pure-white)' }}>
      <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
        <caption style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Feature comparison across pricing tiers
        </caption>
        <thead>
          <tr style={{ background: 'var(--color-gray-100)' }}>
            <th scope="col" style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-900)', position: 'sticky', left: 0, background: 'var(--color-gray-100)', minWidth: 220 }}>
              Feature
            </th>
            {comparison.tiers.map((tier) => (
              <th
                key={tier.slug}
                scope="col"
                style={{
                  padding: '0.85rem 0.75rem',
                  textAlign: 'center',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: tier.slug === currentSlug ? 'var(--color-sky-blue)' : 'var(--color-gray-900)',
                  minWidth: 110,
                }}
              >
                {tier.name}
                {tier.slug === currentSlug ? (
                  <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', marginTop: '0.15rem' }}>Current</div>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {comparison.rows.map((row, index) => {
            const rowBg = index % 2 === 0 ? 'var(--color-pure-white)' : 'var(--color-gray-50, #fafafa)'
            return (
            <tr key={row.feature} style={{ borderTop: '1px solid var(--color-gray-200)', background: rowBg }}>
              <th scope="row" style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 'var(--font-weight-normal)', color: 'var(--color-gray-800)', position: 'sticky', left: 0, background: rowBg }}>
                {row.feature}
              </th>
              {comparison.tiers.map((tier) => (
                <td key={tier.slug} style={{ padding: '0.75rem 0.75rem', textAlign: 'center', color: 'var(--color-gray-700)', background: tier.slug === currentSlug ? 'rgba(0, 174, 239, 0.06)' : undefined }}>
                  <ComparisonValue value={row.values[tier.slug]} />
                </td>
              ))}
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function SubscriptionsManage() {
  const { user, updateSubscriptionTier } = useAuthStore()
  const vendorId = user?.vendorId
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
    confirmText?: string
    cancelText?: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  // Plan change confirmation state
  const [planChangeConfirm, setPlanChangeConfirm] = useState<{
    isOpen: boolean
    planName: string
    planId: string
  }>({
    isOpen: false,
    planName: '',
    planId: '',
  })

  // Cancel subscription confirmation state
  const [cancelConfirm, setCancelConfirm] = useState(false)

  // Handle subscription return
  useEffect(() => {
    const subscriptionParam = searchParams.get('subscription')
    if (subscriptionParam === 'success') {
      const actionParam = searchParams.get('action')
      const successMessage = actionParam === 'resubscribed'
        ? 'Subscription reactivated successfully!'
        : actionParam === 'plan_changed'
          ? 'Subscription plan changed successfully!'
          : 'Subscription created successfully!'
      posthog.capture('subscription_completed', { action: actionParam || 'new' })
      setSearchParams({})

      // Sync with Stripe first (webhook may not have fired yet), then show success
      if (vendorId) {
        api.vendors.getSubscriptionStatus(vendorId, { sync: true }).then(() => {
          queryClient.invalidateQueries({ queryKey: ['subscription-transactions'] })
          queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
          setShowPlans(false)
          setAlertModal({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: successMessage,
          })
        }).catch(() => {
          // Sync failed — still show success and let normal refetch handle it
          queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
          setShowPlans(false)
          setAlertModal({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: successMessage,
          })
        })
      }
    } else if (subscriptionParam === 'cancelled') {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        title: 'Subscription Cancelled',
        message: 'Your subscription has been cancelled.',
        onConfirm: () => {
          queryClient.invalidateQueries({ queryKey: ['subscription-transactions'] })
          queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
          setAlertModal((prev) => ({ ...prev, isOpen: false }))
        },
      })
      setSearchParams({})
    }
  }, [searchParams, setSearchParams, queryClient, vendorId])

  // Subscription Status
  const { data: subscriptionStatus, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription-status', vendorId],
    queryFn: () => api.vendors.getSubscriptionStatus(vendorId!),
    enabled: !!vendorId,
  })

  // Keep auth store tier in sync with subscription status
  useEffect(() => {
    const tier = subscriptionStatus?.data?.data?.planTier
    if (tier !== undefined && tier !== user?.subscriptionTier) {
      updateSubscriptionTier(tier)
    }
  }, [subscriptionStatus?.data?.data?.planTier, user?.subscriptionTier, updateSubscriptionTier])

  // Available Plans (vendor-specific endpoint)
  const { data: plansData, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['subscription-plans', vendorId],
    queryFn: () => api.vendors.getAvailablePlans(vendorId!),
    enabled: !!vendorId,
  })

  const isLoading = isLoadingSubscription || isLoadingPlans

  const allPlans = plansData?.data?.data?.plans || []

  // Billing interval toggle
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month')
  const plans = allPlans.filter((p: any) => (p.interval || p.pricing?.interval) === billingInterval)

  // Track which plan is currently being subscribed to
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null)
  const [changingPlanId, setChangingPlanId] = useState<string | null>(null)
  const [showPlans, setShowPlans] = useState(false)
  const [showFeatureComparison, setShowFeatureComparison] = useState(false)



  // Subscribe to Plan Mutation
  const subscribeMutation = useMutation({
    mutationFn: (planId: string) => {
      setSubscribingPlanId(planId)
      const plan = allPlans.find((p: any) => p.planId === planId || p.id === planId)
      posthog.capture('subscription_started', { plan_id: planId, plan_tier: plan?.tier })
      return api.vendors.subscribe(vendorId!, {
        planId,
        psp: 'stripe',
      })
    },
    onSuccess: (response) => {
      if (response.data?.data?.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl
      } else {
        setAlertModal({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: 'Successfully subscribed to plan!',
          onConfirm: () => {
            window.location.reload()
          },
        })
      }
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to subscribe to plan',
      })
      setSubscribingPlanId(null) // Reset on error
    },
    onSettled: () => {
      setSubscribingPlanId(null) // Reset when mutation completes (success or error)
    },
  })

  // Cancel Subscription Mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: (immediately: boolean = false) => {
      return api.vendors.cancelSubscription(vendorId!, { immediately })
    },
    onSuccess: () => {
      posthog.capture('subscription_cancelled')
      queryClient.invalidateQueries({ queryKey: ['subscription-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Subscription cancelled successfully',
        onConfirm: () => {
          setAlertModal((prev) => ({ ...prev, isOpen: false }))
        },
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to cancel subscription',
      })
    },
  })

  // Change Subscription Mutation
  const changeSubscriptionMutation = useMutation({
    mutationFn: (planId: string) => {
      setChangingPlanId(planId)
      posthog.capture('subscription_changed', { plan_id: planId })
      return api.vendors.changeSubscription(vendorId!, { planId })
    },
    onSuccess: (response) => {
      setChangingPlanId(null)
      if (response.data?.data?.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl
      } else {
        queryClient.invalidateQueries({ queryKey: ['subscription-transactions'] })
        queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
        setAlertModal({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: 'Subscription changed successfully',
          onConfirm: () => {
            setAlertModal((prev) => ({ ...prev, isOpen: false }))
          },
        })
      }
    },
    onError: (error: any) => {
      setChangingPlanId(null)
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to change subscription',
      })
    },
  })

  if (isLoading) {
    return (
      <div>
        <h2 style={{ marginBottom: '2rem' }}>Manage</h2>
        <CardSkeleton />
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Subscription Plans</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '1.5rem' }}>
            <PlanCardSkeleton />
            <PlanCardSkeleton />
            <PlanCardSkeleton />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginBottom: '2rem' }}>Manage</h2>

      <div className="card" style={{ border: `1px solid var(--color-gray-200)`, marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)' }}>Subscriptions</h3>
        </div>

        <div>

          {subscriptionStatus?.data?.data?.hasSubscription && !showPlans ? (
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(152, 202, 63, 0.1) 0%, rgba(0, 174, 239, 0.1) 100%)',
              borderRadius: 'var(--radius-lg)',
              marginBottom: '2rem',
              border: `1px solid var(--color-lime-green)`,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              maxWidth: '600px',
              position: 'relative'
            }}>
              <span style={{
                position: 'absolute',
                top: '1.5rem',
                right: '1.5rem',
                padding: '0.25rem 0.75rem',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                background: subscriptionStatus.data.data.status === 'cancelled'
                  ? 'var(--color-coral-red)'
                  : subscriptionStatus.data.data.status === 'active'
                    ? 'var(--color-lime-green)'
                    : 'var(--color-gray-500)',
                color: 'var(--color-pure-white)',
                textTransform: 'capitalize'
              }}>{subscriptionStatus.data.data.status || 'Unknown'}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-900)' }}>
                  {subscriptionStatus.data.data.planName || 'Current Plan'}
                </h4>
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', lineHeight: 'var(--leading-relaxed)' }}>
                <p style={{ margin: 0 }}>
                  Your next billing date is <strong style={{ color: 'var(--color-gray-900)', fontWeight: 'var(--font-weight-semibold)' }}>
                    {subscriptionStatus.data.data.nextBillingDate || 'N/A'}
                  </strong>
                </p>
              </div>
              {subscriptionStatus.data.data.status === 'cancelled' && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: 'rgba(255, 76, 91, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid var(--color-coral-red)`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <AlertCircle size={18} color="var(--color-coral-red)" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                  <div>
                    <p style={{ margin: 0, color: 'var(--color-coral-red)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                      Your subscription has been cancelled.
                    </p>
                    {subscriptionStatus.data.data.subscription?.cancelled_at && (
                      <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-gray-600)', fontSize: 'var(--text-xs)', lineHeight: 'var(--leading-relaxed)' }}>
                        Cancelled on {new Date(subscriptionStatus.data.data.subscription.cancelled_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {subscriptionStatus.data.data.subscription?.cancel_at_period_end && subscriptionStatus.data.data.status !== 'cancelled' && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: 'rgba(255, 76, 91, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid var(--color-coral-red)`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <AlertCircle size={18} color="var(--color-coral-red)" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                  <p style={{ margin: 0, color: 'var(--color-coral-red)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                    Your subscription has been cancelled and will end at the period end. Access continues until then.
                  </p>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowPlans(true)}
                  style={{
                    padding: '0.625rem 1.25rem',
                    backgroundColor: 'var(--color-sky-blue)',
                    color: 'var(--color-pure-white)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    transition: 'all var(--default-transition-duration) var(--ease-in-out)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {subscriptionStatus.data.data.status === 'cancelled' ? 'Resubscribe' : 'Change Plan'}
                </button>
                {subscriptionStatus.data.data.status !== 'cancelled' && !subscriptionStatus.data.data.subscription?.cancel_at_period_end && (
                  <button
                    onClick={() => {
                      setCancelConfirm(true)
                    }}
                    disabled={cancelSubscriptionMutation.isPending}
                    style={{
                      padding: '0.625rem 1.25rem',
                      backgroundColor: 'var(--color-coral-red)',
                      color: 'var(--color-pure-white)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      cursor: cancelSubscriptionMutation.isPending ? 'not-allowed' : 'pointer',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      transition: 'all var(--default-transition-duration) var(--ease-in-out)',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      opacity: cancelSubscriptionMutation.isPending ? 0.7 : 1
                    }}
                  >
                    {cancelSubscriptionMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* No subscription prompt handled outside the card, opens modal */}
        </div>
      </div>

      {/* Plans in modal when changing, or inline prompt when no subscription */}
      {!subscriptionStatus?.data?.data?.hasSubscription && allPlans.length > 0 && !showPlans && (
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-sm)', marginBottom: '1rem' }}>
            You don't have an active subscription.
          </p>
          <button
            onClick={() => setShowPlans(true)}
            style={{
              padding: '0.75rem 2rem',
              background: 'var(--color-sky-blue)',
              color: 'var(--color-pure-white)',
              border: 'none',
              borderRadius: 'var(--radius-full, 9999px)',
              cursor: 'pointer',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--text-sm)',
            }}
          >
            Choose a Plan
          </button>
        </div>
      )}

      {showPlans && (
        <div style={{ marginBottom: '2rem' }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
            <button
              onClick={() => setShowPlans(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-sky-blue)', fontWeight: 'var(--font-weight-medium)', padding: 0, fontSize: 'var(--text-sm)' }}
            >
              Subscription
            </button>
            <span>/</span>
            <span style={{ color: 'var(--color-gray-700)', fontWeight: 'var(--font-weight-medium)' }}>
              {subscriptionStatus?.data?.data?.hasSubscription ? 'Change Plan' : 'Choose a Plan'}
            </span>
          </div>

          {/* Monthly / Annual toggle */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
            <div style={{
              display: 'inline-flex',
              borderRadius: 'var(--radius-full, 9999px)',
              background: 'var(--color-gray-100)',
              padding: '0.25rem',
            }}>
              <button
                onClick={() => setBillingInterval('month')}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: 'var(--radius-full, 9999px)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  background: billingInterval === 'month' ? 'var(--color-gray-900)' : 'transparent',
                  color: billingInterval === 'month' ? 'var(--color-pure-white)' : 'var(--color-gray-600)',
                  transition: 'all 0.2s ease',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('year')}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: 'var(--radius-full, 9999px)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  background: billingInterval === 'year' ? 'var(--color-gray-900)' : 'transparent',
                  color: billingInterval === 'year' ? 'var(--color-pure-white)' : 'var(--color-gray-600)',
                  transition: 'all 0.2s ease',
                }}
              >
                Annual{plansData?.data?.data?.annualDiscountPercent != null ? <span style={{
                  fontSize: 'var(--text-xs)',
                  background: 'var(--color-lime-green)',
                  color: 'var(--color-pure-white)',
                  padding: '0.125rem 0.5rem',
                  borderRadius: 'var(--radius-full, 9999px)',
                  marginLeft: '0.375rem',
                  fontWeight: 'var(--font-weight-semibold)',
                }}>Save {plansData.data.data.annualDiscountPercent}%</span> : null}
              </button>
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginTop: '-1rem', marginBottom: '1.5rem' }}>
            No commissions. Suppliers keep 100% of booking revenue.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '1.5rem' }}>
            {plans.map((plan: any) => (
              <div
                key={plan.planId}
                style={{
                  border: subscriptionStatus?.data?.data?.planId === plan.planId
                    ? `2px solid var(--color-sky-blue)`
                    : plan.highlightBadge
                      ? `2px solid var(--color-sky-blue)`
                      : `1px solid var(--color-gray-300)`,
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.75rem',
                  paddingTop: plan.highlightBadge ? '2rem' : '1.75rem',
                  display: 'flex',
                  flexDirection: 'column',
                  background: subscriptionStatus?.data?.data?.planId === plan.planId
                    ? 'linear-gradient(135deg, rgba(0, 174, 239, 0.1) 0%, var(--color-pure-white) 100%)'
                    : 'var(--color-pure-white)',
                  boxShadow: subscriptionStatus?.data?.data?.planId === plan.planId
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  transition: 'all var(--default-transition-duration) var(--ease-in-out)',
                  position: 'relative',
                  overflow: 'visible'
                }}
                onMouseEnter={(e) => {
                  if (subscriptionStatus?.data?.data?.planId !== plan.planId) {
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (subscriptionStatus?.data?.data?.planId !== plan.planId) {
                    e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }
                }}
              >
                {/* Badges */}
                {subscriptionStatus?.data?.data?.planId === plan.planId && (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'var(--color-sky-blue)',
                    color: 'var(--color-pure-white)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Current
                  </div>
                )}
                {plan.highlightBadge && subscriptionStatus?.data?.data?.planId !== plan.planId && (
                  <div style={{
                    position: 'absolute',
                    top: '-0.75rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-lime-green)',
                    color: 'var(--color-pure-white)',
                    padding: '0.25rem 1rem',
                    borderRadius: 'var(--radius-full, 9999px)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    whiteSpace: 'nowrap'
                  }}>
                    {plan.highlightBadge}
                  </div>
                )}
                <h4 style={{
                  marginBottom: '0.25rem',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-gray-900)'
                }}>{plan.name}</h4>
                {plan.description && (
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-gray-500)',
                    marginBottom: '1rem',
                    lineHeight: 'var(--leading-relaxed)',
                    marginTop: 0
                  }}>
                    {plan.description}
                  </p>
                )}
                <div style={{ marginBottom: '1.5rem' }}>
                  {(() => {
                    const monthlyPrice = plan.pricing?.amount || 0;
                    const isAnnual = billingInterval === 'year';
                    return (
                      <>
                        <span style={{
                          fontSize: 'var(--text-4xl)',
                          fontWeight: 'var(--font-weight-bold)',
                          color: 'var(--color-gray-900)',
                          lineHeight: 1
                        }}>
                          ${Number(monthlyPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span style={{
                          fontSize: 'var(--text-base)',
                          color: 'var(--color-gray-500)',
                          fontWeight: 'var(--font-weight-normal)',
                          marginLeft: '0.25rem'
                        }}>
                          /mo
                        </span>
                        {isAnnual && monthlyPrice > 0 && (
                          <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
                            ${(Number(monthlyPrice) * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} billed annually
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  background: 'var(--color-gray-100)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-gray-800)',
                  lineHeight: 1.45,
                }}>
                  {plan.bookingAllowance == null
                    ? 'No booking cap'
                    : `Up to ${Number(plan.bookingAllowance).toLocaleString('en-US')} bookings/mo`}
                  {plan.bookingOveragePerUnit != null
                    ? `, then $${Number(plan.bookingOveragePerUnit).toFixed(2)}/booking`
                    : ''}
                  <br />
                  ${Number(plan.aiCreditsIncludedUSD || 0)} in AI credits{' '}
                  {plan.aiCreditsAreTrial ? '— one-time 14-day trial' : 'included/mo'}
                  {plan.bookingAllowanceNote ? (
                    <div style={{ marginTop: '0.25rem', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                      {plan.bookingAllowanceNote}
                    </div>
                  ) : null}
                </div>
                <ul style={{
                  fontSize: 'var(--text-sm)',
                  marginBottom: '1.5rem',
                  paddingLeft: '1.5rem',
                  flex: 1,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {plan.includesPlanName && (
                    <li style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-gray-500)', fontStyle: 'italic' }}>
                      <span>All features in {plan.includesPlanName}, plus:</span>
                    </li>
                  )}
                  {(plan.features || []).map((feature: string, idx: number) => (
                    <li key={idx} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-gray-700)' }}>
                      <CheckCircle2 size={16} color="var(--color-lime-green)" style={{ flexShrink: 0 }} />
                      <span>{feature === 'Trust Score' ? 'Trust Score™' : feature}</span>
                    </li>
                  ))}
                </ul>
                {(() => {
                  const hasSubscription = subscriptionStatus?.data?.data?.hasSubscription
                  const isCurrentPlan = subscriptionStatus?.data?.data?.planId === plan.planId

                  // If no subscription, show subscribe button
                  if (!hasSubscription) {
                    return (
                      <button
                        onClick={() => subscribeMutation.mutate(plan.planId)}
                        disabled={subscribingPlanId === plan.planId || plan.status !== 'active'}
                        style={{
                          marginTop: 'auto',
                          width: '100%',
                          padding: '0.875rem 1.25rem',
                          background: plan.status !== 'active' || subscribingPlanId === plan.planId
                            ? 'var(--color-gray-400)'
                            : plan.highlightBadge
                              ? 'var(--color-sky-blue)'
                              : 'transparent',
                          color: plan.status !== 'active' || subscribingPlanId === plan.planId
                            ? 'var(--color-pure-white)'
                            : plan.highlightBadge
                              ? 'var(--color-pure-white)'
                              : 'var(--color-sky-blue)',
                          border: plan.highlightBadge || plan.status !== 'active' || subscribingPlanId === plan.planId
                            ? 'none'
                            : '2px solid var(--color-sky-blue)',
                          borderRadius: 'var(--radius-full, 9999px)',
                          cursor: plan.status !== 'active' || subscribingPlanId === plan.planId ? 'not-allowed' : 'pointer',
                          fontWeight: 'var(--font-weight-semibold)',
                          fontSize: 'var(--text-sm)',
                          opacity: subscribingPlanId === plan.planId ? 0.7 : 1,
                          transition: 'all var(--default-transition-duration) var(--ease-in-out)',
                        }}
                      >
                        {subscribingPlanId === plan.planId ? 'Processing...' :
                          plan.status !== 'active' ? 'Unavailable' :
                            plan.ctaLabel || 'Subscribe'}
                      </button>
                    )
                  }

                  // If has subscription and this is current plan
                  if (isCurrentPlan) {
                    return (
                      <button
                        disabled
                        style={{
                          marginTop: 'auto',
                          width: '100%',
                          padding: '0.875rem 1.25rem',
                          background: 'var(--color-gray-400)',
                          color: 'var(--color-white)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'not-allowed',
                          fontWeight: 'var(--font-weight-medium)',
                          fontSize: 'var(--text-base)',
                          boxShadow: 'none'
                        }}
                      >
                        Current Plan
                      </button>
                    )
                  }

                  // If has subscription and this is a different plan
                  return (
                    <button
                      onClick={() => {
                        setPlanChangeConfirm({
                          isOpen: true,
                          planName: plan.name,
                          planId: plan.planId,
                        })
                      }}
                      disabled={changingPlanId === plan.planId || plan.status !== 'active'}
                      style={{
                        marginTop: 'auto',
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        background: plan.status !== 'active' || changingPlanId === plan.planId
                          ? 'var(--color-gray-400)'
                          : plan.highlightBadge
                            ? 'var(--color-sky-blue)'
                            : 'transparent',
                        color: plan.status !== 'active' || changingPlanId === plan.planId
                          ? 'var(--color-pure-white)'
                          : plan.highlightBadge
                            ? 'var(--color-pure-white)'
                            : 'var(--color-sky-blue)',
                        border: plan.highlightBadge || plan.status !== 'active' || changingPlanId === plan.planId
                          ? 'none'
                          : '2px solid var(--color-sky-blue)',
                        borderRadius: 'var(--radius-full, 9999px)',
                        cursor: plan.status !== 'active' || changingPlanId === plan.planId ? 'not-allowed' : 'pointer',
                        fontWeight: 'var(--font-weight-semibold)',
                        fontSize: 'var(--text-sm)',
                        opacity: changingPlanId === plan.planId ? 0.7 : 1,
                        transition: 'all var(--default-transition-duration) var(--ease-in-out)',
                      }}
                    >
                      {changingPlanId === plan.planId ? 'Changing...' :
                        plan.status !== 'active' ? 'Unavailable' :
                          'Change to This Plan'}
                    </button>
                  )
                })()}
              </div>
            ))}
          </div>

          {(() => {
            const comparison = plansData?.data?.data?.comparison as PlanComparison | undefined
            if (!comparison || comparison.rows.length === 0) return null
            return (
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowFeatureComparison((open) => !open)}
                aria-expanded={showFeatureComparison}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.75rem',
                  background: 'var(--color-gray-900)',
                  color: 'var(--color-pure-white)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                <Table size={16} aria-hidden="true" />
                {showFeatureComparison ? 'Hide feature comparison' : 'Compare all features'}
              </button>
              {showFeatureComparison && (
                <div style={{ marginTop: '1.25rem', textAlign: 'left' }}>
                  <FeatureComparison
                    comparison={comparison}
                    currentSlug={allPlans.find((plan: any) => plan.planId === subscriptionStatus?.data?.data?.planId)?.slug}
                  />
                </div>
              )}
            </div>
            )
          })()}
        </div>
      )}

      {/* Alert Modals */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => {
          // Invalidate and refetch subscription-related queries when modal is closed
          queryClient.invalidateQueries({ queryKey: ['subscription-transactions'] })
          queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
          setAlertModal((prev) => ({ ...prev, isOpen: false }))
        }}
        onConfirm={alertModal.onConfirm}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />

      <AlertModal
        isOpen={planChangeConfirm.isOpen}
        onClose={() => setPlanChangeConfirm({ ...planChangeConfirm, isOpen: false })}
        onConfirm={() => {
          changeSubscriptionMutation.mutate(planChangeConfirm.planId)
          setPlanChangeConfirm({ ...planChangeConfirm, isOpen: false })
        }}
        type="confirm"
        title="Change Subscription Plan"
        message={`Are you sure you want to change to ${planChangeConfirm.planName}? Your subscription will be updated immediately.`}
        confirmText="Change Plan"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={cancelConfirm}
        onClose={() => setCancelConfirm(false)}
        onConfirm={() => {
          cancelSubscriptionMutation.mutate(false)
          setCancelConfirm(false)
        }}
        type="confirm"
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? It will remain active until the end of the billing period."
        confirmText="Cancel Subscription"
        cancelText="Keep Subscription"
      />
    </div>
  )
}

export default function VendorDashboardPage() {
  return (
    <div className="container">
      <Routes>
        <Route index element={<VendorOverview />} />
        <Route path="payments/manage" element={<PaymentsManage />} />
        <Route path="payments/link-account" element={<PaymentsLinkAccount />} />
        <Route path="subscriptions/transactions" element={<SubscriptionsTransactions />} />
        <Route path="subscriptions/manage" element={<SubscriptionsManage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/experiences" element={<ProductManagePage />} />
        {/* Add product route removed — products synced from provider */}
        <Route path="inventory/experiences/edit/:productId" element={<EditProductPage />} />
        <Route path="inventory/feeds/:feedId/products" element={<FeedProductsPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="feed-settings" element={<AddonPlaceholder feature="Feed Settings & API Keys" />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="supplier-agreement" element={<SupplierAgreementPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="oauth-clients" element={<OAuthClientsPage />} />
        <Route path="ai-connections" element={<AiConnectionsPage />} />
        <Route path="add-ons" element={<AddOnsPage />} />
        <Route path="add-ons/ai-booking-agent" element={<AgentSettingsPage />} />
        <Route path="add-ons/ai-booking-agent/transcripts" element={<AgentTranscriptsPage />} />
        <Route path="widget-setup" element={<AddonPlaceholder feature="Website Widget" />} />
      </Routes>
    </div>
  )
}

